import { useConfirmation } from '@/hooks/useConfirmation';
import {
  setBlockCollapsed,
  toggleBlockCollapsed,
  useIsBlockCollapsed,
} from '@/models/commercial-proposals/blocks-collapsed.store';
import {
  BlockTypes,
  IBlock,
  IServiceBlockConfig,
  useBlocksActions,
  useCellActions,
  useSelectedBlock,
  useSelectedCell,
} from '@/models/commercial-proposals/blocks.store';
import { Draggable, DraggableProvided } from '@hello-pangea/dnd';
import { ActionIcon, Flex, Group, Paper, Text, Tooltip } from '@mantine/core';
import { Fragment, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { PiDotsNine, PiPencil, PiTrash } from 'react-icons/pi';

const staticBlockTypes: BlockTypes[] = [BlockTypes.TitlePage];

export default function BlockBase({
  block,
  index,
  children,
  settings,
  rightActions,
  extraActions,
}: {
  block: IBlock;
  index: number;
  children: React.ReactNode;
  settings: ReactNode;
  rightActions?: ReactNode;
  extraActions?: ReactNode;
}) {
  const selectedBlock = useSelectedBlock();
  const selectedCell = useSelectedCell();
  const { select: selectBlock, remove: removeBlock } = useBlocksActions();
  const { select: selectCell } = useCellActions();
  const isSelected = selectedBlock === block.uuid;
  const isCollapsed = useIsBlockCollapsed(block.uuid);
  const isStatic = staticBlockTypes.includes(block.type);
  const { confirm, ConfirmationDialog } = useConfirmation();

  const renderSettingsPortal = () => {
    if (!isSelected || !settings || selectedCell) return null;

    const portalTarget = document.getElementById('settings-portal');
    if (!portalTarget) return null;

    return createPortal(settings, portalTarget);
  };

  const renderBlock = (provided?: DraggableProvided) => (
    <Paper
      {...(provided && { ...provided.draggableProps, ref: provided.innerRef })}
      p="xs"
      mb="xs"
      withBorder
      styles={{
        root: {
          background:
            block.type !== BlockTypes.TitlePage &&
            (block.config as IServiceBlockConfig).nonInteractive
              ? 'light-dark(rgba(250, 255, 173, 0.29), rgba(255, 235, 59, 0.15))'
              : 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
          borderRadius: '5px',
          boxShadow: isSelected
            ? selectedCell
              ? 'inset 0px 0px 0px 2px var(--mantine-color-gray-5)'
              : 'inset 0px 0px 0px 2px var(--mantine-color-blue-2)'
            : 'light-dark(0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.4))',
        },
      }}
    >
      <Flex justify="space-between" align="center">
        <Group gap={0}>
          {!isStatic && (
            <Flex {...provided?.dragHandleProps}>
              <PiDotsNine
                size={16}
                style={{
                  color: 'var(--mantine-color-gray-6)',
                }}
              />
            </Flex>
          )}
          {settings && (
            <Tooltip label="Edit Block">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => {
                  if (isSelected) {
                    if (selectedCell) {
                      selectCell(null);
                    } else {
                      selectBlock(null);
                    }
                  } else {
                    selectBlock(block.uuid);
                    selectCell(null);
                    if (isCollapsed) {
                      setBlockCollapsed(block.uuid, false);
                    }
                  }
                }}
              >
                <PiPencil size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {!isStatic && (
            <Tooltip label="Delete Block">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={handleDeleteBlock}
              >
                <PiTrash size={16} color="red" />
              </ActionIcon>
            </Tooltip>
          )}
          {extraActions}
          <Text size="xs" fw={500} pl={!isStatic || settings ? 'xs' : 0}>
            {block.title}
          </Text>
        </Group>
        <Group gap={2}>
          {!isCollapsed && rightActions}
          <Tooltip label={isCollapsed ? 'Expand block' : 'Collapse block'}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => toggleBlockCollapsed(block.uuid)}
            >
              {isCollapsed ? (
                <BiChevronUp size={16} />
              ) : (
                <BiChevronDown size={16} />
              )}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Flex>
      {!isCollapsed && children}
    </Paper>
  );

  const handleDeleteBlock = async () => {
    const confirmed = await confirm({
      title: 'Delete Block',
      message: `Are you sure you want to delete the "${block.type}" block?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (confirmed) {
      removeBlock(block.uuid);
    }
  };

  return (
    <>
      {isStatic ? (
        <Fragment key={block.uuid}>{renderBlock()}</Fragment>
      ) : (
        <Draggable draggableId={block.uuid} index={index}>
          {renderBlock}
        </Draggable>
      )}
      {renderSettingsPortal()}
      <ConfirmationDialog />
    </>
  );
}
